import { createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import {
  centerOnActiveEmployer,
  centerOnActiveEmployerComplete,
  closeConfirmDeleteOrganizationDialog,
  closeModifyOrganizationDialog,
  closeProjectOrganizationView,
  openConfirmDeleteOrganizationDialog,
  openModifyOrganizationDialog,
  resetOrganizationChartPosition,
  resetOrganizationChartPositionComplete,
  setChartLayoutDirection,
  setOrganizationBusinessIdToAdd,
  setOrganizationBusinessNameToAdd,
  setSearchInputValue,
  setSelectedOrganizationChartMode,
  setShowEmployees,
  setSubcontractorBusinessAddType,
} from './actions/organizationChart.actions';
import { addProjectOrganizationChartClientForBusiness } from './thunks/addProjectOrganizationChartClientForBusiness.thunk';
import { addSubcontractorExistingBusiness } from './thunks/addSubcontractorExistingBusiness.thunk';
import { addSubcontractorNewBusiness } from './thunks/addSubcontractorNewBusiness.thunk';
import { approveProjectOrganization } from './thunks/approveProjectOrganization.thunk';
import { deleteBusinessOrganizationFromProject } from './thunks/deleteBusinessOrganizationFromProject.thunk';
import { initProjectOrganizationChart } from './thunks/initProjectOrganizationChart.thunk';
import { loadOrganizationChartBusinessInformationByBusinessId } from './thunks/loadBusinessInformationByBusinessId.thunk';
import { loadProjectOrganizationChartItemOfActiveEmployer } from './thunks/loadProjectOrganizationChartItemOfActiveEmployer.thunk';
import { loadProjectOrganizationChartItems } from './thunks/loadProjectOrganizationChartItems.thunk';
import { rejectProjectOrganization } from './thunks/rejectProjectOrganization.thunk';

const organizationChartItemsAdapter = createEntityAdapter();

export const organizationChartSlice = createSlice({
  name: 'organizationCharts',
  initialState: {
    organizationChartItems: organizationChartItemsAdapter.getInitialState(),
    loading: false,
    isUpdatingOrganizationChart: false,
    isModifyOrganizationDialogOpen: false,
    isConfirmDeleteOrganizationDialogOpen: false,
    modifyOrganizationDialogMode: '',
    modificationTargetId: '',
    modificationSourceId: '',
    shouldResetChart: false,
    shouldCenterOnActiveEmployer: false,
    selectedOrganizationChartMode: 'HIERARCHY',
    selectedChartLayoutDirection: 'LR',
    organizationBusinessIdToAdd: '',
    organizationBusinessNameToAdd: '',
    subcontractorBusinessAddType: 'EXISTING',
    isLoadingBusinessInformation: false,
    searchInputValue: '',
    showEmployees: true,
  },
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadProjectOrganizationChartItems.fulfilled, (state, action) => {
        organizationChartItemsAdapter.setMany(
          state.organizationChartItems,
          action.payload
        );
      })
      .addCase(initProjectOrganizationChart.pending, (state) => {
        state.loading = true;
      })
      .addCase(initProjectOrganizationChart.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(initProjectOrganizationChart.rejected, (state, action) => {
        state.loading = false;
      })
      .addCase(openModifyOrganizationDialog, (state, action) => {
        const { mode, targetId, sourceId } = action.payload;
        state.isModifyOrganizationDialogOpen = true;
        state.modifyOrganizationDialogMode = mode;
        state.modificationTargetId = targetId;
        state.modificationSourceId = sourceId || '';
      })
      .addCase(closeModifyOrganizationDialog, (state) => {
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        state.modificationTargetId = '';
        state.modificationSourceId = '';
        state.organizationBusinessIdToAdd = '';
        state.organizationBusinessNameToAdd = '';
        state.subcontractorBusinessAddType = 'EXISTING';
      })
      .addCase(openConfirmDeleteOrganizationDialog, (state, action) => {
        const { targetId } = action.payload;

        state.isConfirmDeleteOrganizationDialogOpen = true;
        state.modificationTargetId = targetId;
      })
      .addCase(closeConfirmDeleteOrganizationDialog, (state) => {
        state.isConfirmDeleteOrganizationDialogOpen = false;
        state.modificationTargetId = '';
      })

      .addCase(
        deleteBusinessOrganizationFromProject.pending,
        (state, action) => {
          state.isUpdatingOrganizationChart = true;
        }
      )
      .addCase(
        deleteBusinessOrganizationFromProject.fulfilled,
        (state, action) => {
          const { deletedOrganizationChartItem } = action.payload;

          organizationChartItemsAdapter.setOne(
            state.organizationChartItems,
            deletedOrganizationChartItem
          );

          state.isUpdatingOrganizationChart = false;
          state.isConfirmDeleteOrganizationDialogOpen = false;
        }
      )
      .addCase(
        deleteBusinessOrganizationFromProject.rejected,
        (state, action) => {
          state.isUpdatingOrganizationChart = false;
        }
      )
      .addCase(resetOrganizationChartPosition, (state) => {
        state.shouldResetChart = true;
      })
      .addCase(resetOrganizationChartPositionComplete, (state) => {
        state.shouldResetChart = false;
      })
      .addCase(centerOnActiveEmployer, (state) => {
        state.shouldCenterOnActiveEmployer = true;
      })
      .addCase(centerOnActiveEmployerComplete, (state) => {
        state.shouldCenterOnActiveEmployer = false;
      })
      .addCase(setChartLayoutDirection, (state, action) => {
        state.selectedChartLayoutDirection = action.payload;
      })
      .addCase(closeProjectOrganizationView, (state, action) => {
        state.selectedChartLayoutDirection = 'LR';
        state.searchInputValue = '';
      })
      .addCase(setOrganizationBusinessIdToAdd, (state, action) => {
        state.organizationBusinessIdToAdd = action.payload;
      })
      .addCase(setOrganizationBusinessNameToAdd, (state, action) => {
        state.organizationBusinessNameToAdd = action.payload;
      })
      .addCase(setSubcontractorBusinessAddType, (state, action) => {
        state.subcontractorBusinessAddType = action.payload;
      })
      .addCase(
        loadOrganizationChartBusinessInformationByBusinessId.pending,
        (state, action) => {
          state.isLoadingBusinessInformation = true;
        }
      )
      .addCase(
        loadOrganizationChartBusinessInformationByBusinessId.fulfilled,
        (state, action) => {
          const { businessName } = action.payload;
          state.organizationBusinessNameToAdd = businessName;
          state.isLoadingBusinessInformation = false;
        }
      )
      .addCase(
        loadOrganizationChartBusinessInformationByBusinessId.rejected,
        (state, action) => {
          state.isLoadingBusinessInformation = false;
        }
      )
      .addCase(setSearchInputValue, (state, action) => {
        state.searchInputValue = action.payload;
      })
      .addCase(approveProjectOrganization.fulfilled, (state, action) => {
        organizationChartItemsAdapter.setOne(
          state.organizationChartItems,
          action.payload
        );
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        enqueueSnackbar('Organisaatio hyväksytty projektille', {
          variant: 'success',
        });
      })
      .addCase(approveProjectOrganization.rejected, (state, action) => {
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        enqueueSnackbar('Organisaation hyväksyntä epäonnistui', {
          variant: 'error',
        });
      })
      .addCase(rejectProjectOrganization.fulfilled, (state, action) => {
        organizationChartItemsAdapter.setOne(
          state.organizationChartItems,
          action.payload
        );
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        enqueueSnackbar('Organisaatio hylätty projektilta', {
          variant: 'success',
        });
      })
      .addCase(rejectProjectOrganization.rejected, (state, action) => {
        state.isModifyOrganizationDialogOpen = false;
        state.modifyOrganizationDialogMode = '';
        enqueueSnackbar('Organisaation hylkäys epäonnistui', {
          variant: 'error',
        });
      })
      .addCase(
        loadProjectOrganizationChartItemOfActiveEmployer.fulfilled,
        (state, action) => {
          organizationChartItemsAdapter.addMany(
            state.organizationChartItems,
            action.payload
          );
        }
      )
      .addCase(setSelectedOrganizationChartMode, (state, action) => {
        state.selectedOrganizationChartMode = action.payload;
      })
      .addCase(setShowEmployees, (state, action) => {
        state.showEmployees = action.payload;
      })
      .addMatcher(
        isAnyOf(
          addSubcontractorExistingBusiness.pending,
          addSubcontractorNewBusiness.pending,
          addProjectOrganizationChartClientForBusiness.pending
        ),
        (state, action) => {
          state.isUpdatingOrganizationChart = true;
        }
      )
      .addMatcher(
        isAnyOf(
          addSubcontractorExistingBusiness.fulfilled,
          addSubcontractorNewBusiness.fulfilled,
          addProjectOrganizationChartClientForBusiness.fulfilled
        ),
        (state, action) => {
          organizationChartItemsAdapter.setMany(
            state.organizationChartItems,
            action.payload
          );
          state.isModifyOrganizationDialogOpen = false;
          state.modifyOrganizationDialogMode = '';
          state.modificationTargetId = '';
          state.modificationSourceId = '';
          state.isUpdatingOrganizationChart = false;
          state.organizationBusinessIdToAdd = '';
          state.organizationBusinessNameToAdd = '';
        }
      )
      .addMatcher(
        isAnyOf(
          addSubcontractorExistingBusiness.rejected,
          addSubcontractorNewBusiness.rejected,
          addProjectOrganizationChartClientForBusiness.rejected
        ),
        (state, action) => {
          state.isUpdatingOrganizationChart = false;
        }
      );
  },
});
